import React, {useEffect, useMemo, useRef, useState} from 'react';
import classNames from 'classnames';
import {mergeRefs} from '@react-aria/utils';

import {Input} from '@time-webkit/all/atoms/input';
import {TypographySize, useTypography} from '@time-webkit/all/hooks/typography';
import {COUNTRIES, type Country} from '@time-webkit/all/utils/phone-codes/constants';
import {EarthIcon} from '@time-webkit/all/icons/earth';
import {DropdownButton} from '@time-webkit/all/molecules/dropdown-button';

import {isValidPhoneNumber} from 'libphonenumber-js/core';
import metadata from 'libphonenumber-js/metadata.min.json';

import {useFocusWithin} from '@react-aria/interactions';

import {useMaskito} from '@maskito/react';

import {Image} from '@time-webkit/all/atoms/image';

import {getPhoneCountryCode, maskitoOptions} from './mask';
import styles from './styles.module.css';
import dropdownStyles from './countries-dropdown/styles.module.css';
import {CountriesDropdown} from './countries-dropdown';

export enum PhoneInputError {
    Invalid = 'invalid',
    Empty = 'empty',
}

export const PLUS = '+';

export type Props = {
    className?: string;
    errorMessage?: string;
    onPhoneError?: (error: PhoneInputError) => void;
    onCountrySelected: (phoneCode: string) => void;
} & React.ComponentProps<typeof Input>;

export const PhoneInput = React.forwardRef<HTMLInputElement, Props>((props, ref) => {
    const {onCountrySelected, onPhoneError, value: propsValue = '', ...inputProps} = props;

    const inputRef = useRef<HTMLInputElement>(null);
    const maskitoRef = useMaskito({options: maskitoOptions});
    const containerRef = useRef<HTMLDivElement>(null);

    const [selectedCountry, setSelectedCountry] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const phoneCountryCode = useMemo(
        () => getPhoneCountryCode(propsValue, selectedCountry),
        [propsValue, selectedCountry],
    );

    useEffect(() => {
        if (propsValue === '') {
            setSelectedCountry('');
        }
    }, [propsValue]);

    const handleFocusWithinChange = (state: boolean) => {
        if (state) {
            return;
        }
        if (!propsValue.split('').includes(' ')) {
            onPhoneError?.(PhoneInputError.Empty);
            return;
        }

        if (!isValidPhoneNumber(propsValue, metadata)) {
            onPhoneError?.(PhoneInputError.Invalid);
        }
    };

    const handleSelectCountry = (country: Country) => {
        onCountrySelected(PLUS + country.countryCallingCode);
        setSelectedCountry(country.countryCode);
    };

    const {focusWithinProps} = useFocusWithin({onFocusWithinChange: handleFocusWithinChange});
    const [prefixCls] = useTypography([{size: TypographySize.BodyL}]);

    const toggleDropdown = () => {
        setIsDropdownOpen((prevValue) => !prevValue);

        if (isDropdownOpen) {
            inputRef?.current?.focus();
        }
    };

    const closeDropdown = () => {
        setIsDropdownOpen(false);
        inputRef?.current?.focus();
    };

    const getRightIcon = () => {
        return phoneCountryCode ? (
            <div
                data-testid={`flag-${phoneCountryCode}`}
                className={styles.image_container}
            >
                <Image
                    src={COUNTRIES[phoneCountryCode]?.image}
                    width='21'
                    height='15'
                    alt={`flag-${phoneCountryCode}`}
                    fallback={
                        <EarthIcon
                            width={20}
                            height={20}
                            className={styles.earth}
                        />
                    }
                />
            </div>
        ) : (
            <EarthIcon
                width={20}
                height={20}
                className={styles.earth}
                data-testid='earth'
            />
        );
    };

    return (
        <div
            ref={containerRef}
            {...focusWithinProps}
            className={styles.input_wrapper}
        >
            <Input
                className={styles.input}
                {...inputProps}
                value={propsValue}
                ref={mergeRefs(inputRef, ref, maskitoRef)}
                placeholder='0 (000) 000–00–00'
                prefix={!propsValue && <span className={classNames(prefixCls, styles.prefix)}>+</span>}
                iconRight={
                    <DropdownButton
                        isOpen={isDropdownOpen}
                        onPress={toggleDropdown}
                        className={classNames(dropdownStyles.dropdown_button, {
                            [dropdownStyles.active]: isDropdownOpen,
                        })}
                    >
                        {getRightIcon()}
                    </DropdownButton>
                }
            />
            <CountriesDropdown
                open={isDropdownOpen}
                targetRef={containerRef}
                onSelectCountry={handleSelectCountry}
                onClose={closeDropdown}
            />
        </div>
    );
});
